import { useCallback, useEffect, useRef, useState } from "react";
import { getCalcCache, setCalcCache } from "@/lib/calcCache";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { EmptyState } from "@/components/Metric";
import ProductionTicket from "@/components/ProductionTicket";
import { useRequote } from "@/lib/useRequote";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import { MiscPicker } from "@/components/MiscPicker";
import { ServicesPicker } from "@/components/ServicesPicker";
import { MiscListTab } from "@/components/MiscListTab";
import { OrderLines, newLine } from "@/components/apparel/OrderLines";
import { PlacementsPicker, placementsFromSettings } from "@/components/apparel/PlacementsPicker";
import { ApparelResults } from "@/components/apparel/ApparelResults";
import { GarmentCatalog } from "@/components/apparel/GarmentCatalog";
import { GangSheets } from "@/components/apparel/GangSheets";

export default function Apparel() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const saved = useRef(getCalcCache("apparel_full") || {}).current;
  const [garments, setGarments] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [lines, setLines] = useState(saved.lines || []);
  const [placements, setPlacements] = useState(saved.placements || []);
  const [designFee, setDesignFee] = useState(!!saved.designFee);
  const [embWaived, setEmbWaived] = useState(!!saved.embWaived);
  const [miscRows, setMiscRows] = useState(saved.miscRows || []);
  const [services, setServices] = useState(null);
  const [res, setRes] = useState(saved.res || null);
  const [orderNo, setOrderNo] = useState(saved.orderNo || "");
  const [orderName, setOrderName] = useState(saved.orderName || "");
  const [prodTime, setProdTime] = useState("Standard");
  const [updating, setUpdating] = useState(false);
  const [autoErr, setAutoErr] = useState("");

  const loadGarments = useCallback(() => api.get("/garments").then((r) => { setGarments(r.data); return r.data; }), []);
  useEffect(() => {
    Promise.all([loadGarments(), api.get("/settings")]).then(([g, s]) => {
      setCfg(s.data);
      setLines((cur) => (cur.length ? cur : [newLine(g)]));
      setPlacements((cur) => (cur.length ? cur : placementsFromSettings(s.data.apparel_placements)));
    });
  }, [loadGarments]);

  const calc = async (auto = false) => {
    if (auto) setUpdating(true);
    try {
      const { data } = await api.post("/calc/apparel", {
        lines: lines.map((l) => ({ ...l, sizes: Object.fromEntries(Object.entries(l.sizes || {}).filter(([, v]) => +v > 0)) })),
        placements: placements.filter((p) => p.on).map((p) => ({ key: p.key, label: p.label, method: p.method, w: +p.w || 0, h: +p.h || 0, stitch_count: +p.stitch_count || 0, custom_threads: +p.custom_threads || 0, upcharge: +p.upcharge || 0 })),
        design_fee: designFee, emb_setup_waived: embWaived,
        misc: miscRows.filter((r) => r.material_id && +r.qty > 0).map((r) => ({ material_id: r.material_id, qty: +r.qty })),
        services,
      });
      setRes(data); setAutoErr(""); firstSig.current = sigRef.current;
    } catch (e) {
      const msg = apiErr(e.response?.data?.detail);
      if (auto) setAutoErr(msg); else toast.error(msg);
    }
    setUpdating(false);
  };

  const inputs = { lines, placements, designFee, embWaived };
  const sig = JSON.stringify({ ...inputs, miscRows, services });
  const sigRef = useRef(sig);
  sigRef.current = sig;
  const hasRes = !!res;
  const firstSig = useRef(sig);
  useEffect(() => {
    if (!hasRes || sig === firstSig.current) return undefined;
    firstSig.current = sig;
    const t = setTimeout(() => calc(true), 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, hasRes]);
  useEffect(() => {
    setCalcCache("apparel_full", { lines, placements, designFee, embWaived, miscRows, res, orderNo, orderName });
  }, [lines, placements, designFee, embWaived, miscRows, res, orderNo, orderName]);

  useRequote((rq) => {
    if (Array.isArray(rq.lines) && rq.lines.length) setLines(rq.lines);
    if (Array.isArray(rq.placements) && rq.placements.length) setPlacements(rq.placements);
    if (rq.designFee != null) setDesignFee(rq.designFee);
    if (rq.embWaived != null) setEmbWaived(rq.embWaived);
  }, calc);

  const onRush = useCallback((x) => setProdTime((x.label || "Standard").replace(/\s*\+\d+%$/, "")), []);
  const hasEmb = placements.some((p) => p.on && p.method === "embroidery");
  const methods = [...new Set(placements.filter((p) => p.on).map((p) => (p.method === "dtf" ? "DTF printer" : "Embroidery machine")))].join(" + ");

  return (
    <div data-testid="apparel-page">
      <PageHeader title="Apparel" eyebrow="DTF + Embroidery" subtitle="Mix garments, colours and sizes in one order · placements · customer-supplied · gang sheets" />
      <div className="p-4 sm:p-8">
        <Tabs defaultValue="calc">
          <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto">
            <TabsTrigger value="calc" data-testid="tab-calc" className="rounded-full">Decorated garments</TabsTrigger>
            <TabsTrigger value="sheets" data-testid="tab-gang-sheets" className="rounded-full">Gang sheets</TabsTrigger>
            {isAdmin && <TabsTrigger value="garments" data-testid="tab-garments" className="rounded-full">Garments</TabsTrigger>}
            <TabsTrigger value="misc" data-testid="tab-misc" className="rounded-full">Miscellaneous</TabsTrigger>
          </TabsList>

          <TabsContent value="calc" className="mt-6 grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-6 bg-white border border-slate-200 rounded-xl p-4 sm:p-6 space-y-6">
              {!cfg ? <div className="text-sm text-slate-400">Loading…</div> : (<>
                <OrderLines lines={lines} setLines={setLines} garments={garments} bigSizes={cfg.apparel_big_sizes} />
                <PlacementsPicker placements={placements} setPlacements={setPlacements} />
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><Label className="text-xs">Design / artwork fee</Label><Switch data-testid="apparel-design-fee" checked={designFee} onCheckedChange={setDesignFee} /></div>
                  {hasEmb && <div className="flex items-center justify-between"><Label className="text-xs">Repeat order — embroidery setup already paid</Label><Switch data-testid="apparel-emb-waived" checked={embWaived} onCheckedChange={setEmbWaived} /></div>}
                </div>
              </>)}
              <MiscPicker module="dtf" value={miscRows} onChange={setMiscRows} />
              <ServicesPicker module="dtf" onChange={setServices} />
              <Button data-testid="calc-apparel-button" onClick={() => calc(false)} className="w-full bg-[#2495D3] hover:bg-[#1E7AA9] rounded-lg h-11"><Calculator size={16} className="mr-2" />Generate Quote</Button>
              {res && <p className="text-[11px] text-center text-slate-400 -mt-4" data-testid="apparel-auto-note">{updating ? "Updating…" : autoErr ? <span className="text-red-500">{autoErr}</span> : "Live — the quote updates automatically when you change anything"}</p>}
              {res && (
                <ProductionTicket testid="apparel-ticket" orderNo={orderNo} setOrderNo={setOrderNo} orderName={orderName} setOrderName={setOrderName}
                  machine={methods} material={res.lines.map((l) => `${l.name}${l.color ? ` ${l.color}` : ""}`).join(", ")}
                  rows={res.placements.map((p) => ({ label: p.label, value: p.method === "dtf" ? `DTF ${p.w}×${p.h}"` : `${p.stitch_count} st` }))}
                  total={res.quantity} totalLabel="Total pieces" prodTime={prodTime} />
              )}
            </div>
            <div className="lg:col-span-6">
              {!res ? <EmptyState>Add garments, pick placements and calculate.</EmptyState> : <div className={autoErr ? "opacity-40 pointer-events-none" : ""} data-testid="apparel-results-wrap"><ApparelResults res={res} inputs={inputs} onRush={onRush} /></div>}
            </div>
          </TabsContent>
          <TabsContent value="sheets" className="mt-6"><GangSheets /></TabsContent>
          {isAdmin && <TabsContent value="garments" className="mt-6"><GarmentCatalog garments={garments} reload={loadGarments} /></TabsContent>}
          <TabsContent value="misc" className="mt-6"><MiscListTab module="dtf" /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
